import { ToolSearch, ToolFilters, ToolButton, ToolEmpty } from "./ToolWorkspace";
import { useState, useEffect } from "react";
import { collection, query, orderBy, onSnapshot, doc, updateDoc, getDocs, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { C } from "../constants/theme";
import { toast } from "../hooks/useToast";
import { useConfirm } from "../hooks/useConfirm";
import { Card } from "./ui/Card";
import { Btn } from "./ui/Btn";
import { ChatPedido } from "./ChatPedido";

const fmtData = (ms) => ms
  ? new Date(ms).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
  : "";

// Conversas livres dos clientes com a loja (chat da vitrine, fora de um
// pedido). Cada cliente tem uma: users/{vendedor}/conversas_loja/{uid do cliente}.
export function ConversasLoja({ vendedorId }) {
  const [busca,setBusca] = useState("");
  const [filtro,setFiltro] = useState("todos");
  const [conversas, setConversas] = useState([]);
  const [aberta, setAberta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [excluindo, setExcluindo] = useState(null);
  const [confirmar, modalConfirm] = useConfirm();

  useEffect(() => {
    if (!vendedorId) return;
    const q = query(collection(db, `users/${vendedorId}/conversas_loja`), orderBy("updated_at", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setConversas(lista);
      setLoading(false);
      // mantém a conversa aberta atualizada
      setAberta(prev => prev ? (lista.find(c => c.id === prev.id) || prev) : null);
    }, (e) => { console.error("Erro nas conversas da loja:", e); setLoading(false); });
    return () => unsub();
  }, [vendedorId]);

  // Abrir a conversa (ou chegar mensagem nova com ela aberta) marca como lida.
  useEffect(() => {
    if (!aberta?.naoLidoVendedor) return;
    updateDoc(doc(db, `users/${vendedorId}/conversas_loja/${aberta.id}`), { naoLidoVendedor: false }).catch(() => {});
  }, [vendedorId, aberta?.id, aberta?.naoLidoVendedor]);

  // Apaga a conversa inteira (as mensagens são uma subcoleção: não somem sozinhas)
  const excluirConversa = async (c) => {
    const ok = await confirmar(`Excluir a conversa com ${c.cliente?.nome || "o cliente"}? Isso apaga todas as mensagens. Não dá para desfazer.`);
    if (!ok) return;
    setExcluindo(c.id);
    try {
      const msgs = await getDocs(collection(db, `users/${vendedorId}/conversas_loja/${c.id}/mensagens`));
      await Promise.all(msgs.docs.map(m => deleteDoc(m.ref)));
      await deleteDoc(doc(db, `users/${vendedorId}/conversas_loja/${c.id}`));
      if (aberta?.id === c.id) setAberta(null);
      toast("Conversa excluída", "success");
    } catch (e) {
      console.error("Erro ao excluir conversa:", e);
      toast("Erro ao excluir conversa", "error");
    }
    setExcluindo(null);
  };

  const filtrados = conversas.filter(item => [item.id,item.cliente?.nome,item.cliente?.email,item.ultimaMsg].join(" ").toLowerCase().includes(busca.toLowerCase().trim()) && (filtro === "todos" || (filtro === "naolidos" ? item.naoLidoVendedor : item.status === filtro)));

  if (aberta) {
    return (
      <div className="site-detail" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {modalConfirm}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <button onClick={() => setAberta(null)} style={{ background: "none", border: "none", color: C.accent2, cursor: "pointer", fontSize: 14 }}>
            ← Voltar às mensagens
          </button>
          <Btn small outline color={C.danger} disabled={excluindo === aberta.id} onClick={() => excluirConversa(aberta)}>
            {excluindo === aberta.id ? "⏳ Excluindo..." : "🗑️ Excluir conversa"}
          </Btn>
        </div>
        <Card style={{ padding: 18 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>💬 {aberta.cliente?.nome || "Cliente"}</h3>
          <ChatPedido vendedorId={vendedorId} pedidoId={aberta.id} autor="vendedor" colecao="conversas_loja" altura={340} />
        </Card>
      </div>
    );
  }

  return <div className="tool-module site-inbox">
    {modalConfirm}<ToolSearch value={busca} onChange={setBusca} placeholder="Buscar cliente ou mensagem" /><ToolFilters value={filtro} onChange={setFiltro} options={[{value:"todos",label:"Todas",count:conversas.length},{value:"naolidos",label:"Não lidas",count:conversas.filter(c=>c.naoLidoVendedor).length}]} />
    <div className="tool-item-grid">{loading?<ToolEmpty icon="clientes" title="Carregando conversas…" />:!filtrados.length?<ToolEmpty icon="clientes" title={conversas.length?"Nenhuma conversa neste filtro":"Vamos conversar?"}>Quando um cliente usar “Falar com a loja”, a conversa aparece aqui.</ToolEmpty>:filtrados.map(c=><article className="tool-item" key={c.id}><div className="site-inbox-heading"><span className="tool-eyebrow">{fmtData(c.updated_at)||"CONVERSA"}</span>{c.naoLidoVendedor&&<span className="site-unread">Não lida</span>}</div><h3>{c.cliente?.nome||"Cliente"}</h3><p className="site-message-preview">{c.ultimaMsgAutor==="vendedor"?"Você: ":""}{c.ultimaMsg||"Abra a conversa para ver as mensagens."}</p><div className="tool-actions"><ToolButton secondary onClick={()=>setAberta(c)}>Abrir conversa</ToolButton><ToolButton danger aria-label={"Excluir conversa com "+(c.cliente?.nome||"cliente")} disabled={excluindo===c.id} onClick={()=>excluirConversa(c)}>{excluindo===c.id?"Excluindo…":"Excluir"}</ToolButton></div></article>)}</div>
  </div>;
}
